import { useEffect, useState } from "react";
import { BACKEND_URL } from "../constants";

export function useListAny<T = any>(path: string) {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchAny = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("jwt");
      const response = await fetch(`${BACKEND_URL}${path}`, {
        method: "GET",
        headers: {
          Authorization: token ?? "",
        },
        cache: "no-store"
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const jsonData = (await response.json()) as T;
      setData(jsonData);
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err);
      } else {
        setError(new Error("An unknown error occurred"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAny();
  }, []);

  return { data, isLoading, error, refetch: fetchAny };
}

interface UsePutResult<T, B = any> {
  isLoading: boolean;
  error: Error | null;
  put: (path: string, body: B) => Promise<T | null>;
}

export function usePutAny<T = any, B = any>(): UsePutResult<T, B> {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const put = async (path: string, body: B): Promise<T | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("jwt");

      const response = await fetch(`${BACKEND_URL}${path}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ?? "",
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = (await response.json()) as T;
      return data;
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err);
      } else {
        setError(new Error("An unknown error occurred"));
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return { isLoading, error, put };
}

interface UsePostResult<T, B = any> {
  isLoading: boolean;
  error: Error | null;
  post: (body: B) => Promise<T | null>;
}

export function usePostAny<T = any, B = any>(path: string): UsePostResult<T, B> {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const post = async (body: B): Promise<T | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("jwt");

      const response = await fetch(`${BACKEND_URL}${path}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ?? "",
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = (await response.json()) as T;
      return data;
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err);
      } else {
        setError(new Error("An unknown error occurred"));
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return { isLoading, error, post };
}

export function useGetAsyncAny<T = any>() {
  const fetchAny = async (path: string): Promise<T> => {
    return new Promise((resolve, reject) => {
      const token = localStorage.getItem("jwt");
      fetch(`${BACKEND_URL}${path}`, {
        method: "GET",
        headers: {
          Authorization: token ?? "",
        },
        cache: "no-store"
      })
        .then(response => {
          if (!response.ok) {
            reject(new Error(`HTTP error! Status: ${response.status}`));
          }
          resolve(response.json() as T)
        })
        .catch(err => reject(err))
    })
  }
  return { fetch: fetchAny };
}

interface UseDeleteResult<T> {
  isLoading: boolean;
  error: Error | null;
  del: (path: string) => Promise<T | null>;
}

export function useDeleteAny<T = any>(): UseDeleteResult<T> {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const del = async (path: string): Promise<T | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("jwt");

      const response = await fetch(`${BACKEND_URL}${path}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ?? "",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      // In case the response has no body (e.g. 204 No Content)
      try {
        const data = (await response.json()) as T;
        return data;
      } catch {
        return null;
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err);
      } else {
        setError(new Error("An unknown error occurred"));
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return { isLoading, error, del };
}
